import React, { useState, useMemo, useCallback, useEffect } from "react";
import { graphql, useStaticQuery, withPrefix } from "gatsby";
import Parser from "html-react-parser";
import classNames from "classnames";
import FormSelect from "./elements/form-select";
import FormAutocomplete from "./elements/form-autocomplete";
import { Link } from "./link";
import BannerPushApp from "./banner-push-app";
import { GroupProvider } from "../context/group";
import { CDN_BASE_URL } from "../utility/constants";
import DownloadIcon from "../icons/download-arrow.svg";

const typeMap = {
  0: "userManual",
  50700: "userManual",
  50701: "quickReference",
  50702: "quickGuide",
  50703: "enrollmentGuide",
  50704: "googleHomeGuide",
  50705: "amazonAlexaGuide",
  50706: "userRepairInstructions",
  50707: "ecodesignInformation",
  50708: "installationManual",
  50709: "pictorialManual",
  50710: "instructionsForSafeUse",
  //50711: "manualType50711",
  50712: "manualType50712",
  50713: "manualType50713",
  50714: "manualType50714",
  50715: "manualType50715",
};
const serviceBaseUrl = process.env.GATSBY_BABEL_BASE_URL;

const ManualResult = ({ docType, labelManagerLabels, open = false }) => {
  const [expanded, setExpanded] = useState(open);
  const toggleExpanded = useCallback(() => setExpanded((value) => !value), []);
  const [{ type }] = docType?.items?.length ? docType?.items : [{}];
  const typeLangKey = typeMap[type];

  return (
    <div
      className={classNames(
        `manuali__box-manuale ${expanded ? " open" : ""}`,
        !docType?.items?.length && "d-none"
      )}
    >
      <button
        aria-expanded={expanded}
        onClick={toggleExpanded}
        className="manuali__box-manuale-title"
      >
        {typeLangKey ? labelManagerLabels[typeLangKey] : docType.label}
      </button>

      <div className="manuali__box-manuale-content">
        <div>
          {docType.items.map((item, index) => (
            <a key={index} href={item.filename} className="manuali__pdf-manual">
              {item.lang}
              <DownloadIcon className="ml-auto" />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

const getManuals = async (id, siteMetadata) => {
  const response = await fetch(`${serviceBaseUrl}/getproductmanualsnew/${id}`);
  const data = await response.json();
  const availableLang = [
    "EN",
    siteMetadata.derivedLang || siteMetadata.priceLanguage.split("-")[0].toUpperCase(),
  ];
  if (data && data.GetProductManualsNewResult) {
    const results = data.GetProductManualsNewResult?.filter(
      (manual) => ![771, 772, 8815, 8816, 8825, 8826, 50711]?.includes(manual?.type)
    );
    const groupedManuals = [...new Set(results.map((item) => item.label))]
      .map((label) => ({
        label,
        items: results
          .filter((item) => item.label === label)
          .filter((item) => availableLang.includes(item.lang)),
      }))
      .filter((elem) => !!elem.items.length);
    return groupedManuals;
  } else {
    return [];
  }
};

const getSocDoc = async (sku, pathPrefix, labelManagerLabels) => {
  if (pathPrefix === "/en_GB/") {
    try {
      const response = await fetch(`/headless-api/libretti?sku=${sku}&origin=certs`, {
        method: "GET",
      });
      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      const soc = data?.data?.length
        ? data.data.find((doc) =>
            doc?.ca4_CHG_doc_subtype?.includes("PSTI Statement of Compliance")
          )
        : null;
      const socStructured = soc
        ? [
            {
              items: [{ filename: soc?.ca4_CHG_pc_url, lang: "EN" }],
              label: labelManagerLabels.statementOfCompliance,
            },
          ]
        : [];
      return socStructured;
    } catch (e) {
      console.warn("Missing SoC", e);
    }
  }
  return [];
};

const getDeclarationOfConformity = async (sku, pathPrefix, labelManagerLabels) => {
  try {
    const response = await fetch(`/headless-api/libretti?sku=${sku}&origin=certs`, {
      method: "GET",
    });
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    const doc = data?.data?.length
      ? data.data.filter((doc) =>
          pathPrefix !== "/en_GB/"
            ? doc?.ca4_CHG_doc_subtype?.includes("EU Declaration of Conformity")
            : doc?.ca4_CHG_doc_subtype?.includes("Declaration of Conformity")
        )
      : null;
    const docStructured = doc?.length
      ? [
          {
            items: doc?.map((item) => ({
              filename: item?.ca4_CHG_pc_url,
              lang: item?.ca4_CHG_doc_subtype?.includes("EU") ? "EU" : "UK",
            })),
            label: labelManagerLabels.declarationOfConformity,
          },
        ]
      : [];
    return docStructured;
  } catch (e) {
    console.warn("Missing declaration", e);
  }
  return [];
};

const Manuali = ({ data }) => {
  const [loading, setLoading] = useState(true);
  const [modelli, setModelli] = useState(null);
  const [lineOption, setLineOption] = useState("");
  const [product, setProduct] = useState();
  const [formErrors, setFormErrors] = useState({});
  const [loadingManuals, setLoadingManuals] = useState(false);
  const [manuals, setManuals] = useState(null);
  const pathPrefix = withPrefix("/");

  const {
    site: { siteMetadata },
    allLiferayProductApp,
    labelManagerLabels,
  } = useStaticQuery(graphql`
    {
      site {
        siteMetadata {
          babelPrefix
          priceLanguage
          derivedLang
        }
      }
      labelManagerLabels: labelManagerLabels {
        select
        formModel
        formProductLine
        bookletFound
        bookletNotFound
        bookletFindImage
        bookletSelectModel
        bookletProductCode
        bookletNotAvaiable
        userManual
        quickReference
        quickGuide
        enrollmentGuide
        googleHomeGuide
        amazonAlexaGuide
        userRepairInstructions
        ecodesignInformation
        installationManual
        pictorialManual
        instructionsForSafeUse
        manualType50711
        manualType50712
        manualType50713
        manualType50714
        manualType50715
        howReadProductCode
        declarationOfConformity
        wiringDiagram
        technicalNote
        explodedView
        legalCheckDocuments
        statementOfCompliance
      }
      allLiferayProductApp {
        nodes {
          ...ProductAppFragment
        }
      }
      allLiferayPageLine {
        nodes {
          lineId
          lineConfigurationFieldSet {
            isDeclarationOfConformity
            isDeclarationOfConformityUk
          }
        }
      }
    }
  `);

  const liferayProductApp = useMemo(
    () => allLiferayProductApp?.nodes || [],
    [allLiferayProductApp]
  );

  const serviceLine = useMemo(() => {
    return `${serviceBaseUrl}/getproductslines/${siteMetadata.babelPrefix}/haier`;
  }, [siteMetadata.babelPrefix]);

  const line = useMemo(
    () => (lineOption ? parseInt(lineOption.split("_")[0]) : null),
    [lineOption]
  );
  const lineModels = useMemo(() => {
    return modelli && line
      ? [{ label: "", value: "" }]
          .concat(
            modelli
              .filter((item) => item.id === line)[0]
              .products.map((item) => ({
                label: `${item.name} (${item.id})`,
                value: `${item.id}`,
              }))
          )
          .sort((a, b) => (a.label < b.label ? -1 : 1))
      : [];
  }, [modelli, line]);

  const lineOpts = useMemo(
    () =>
      [
        {
          value: "",
          label: labelManagerLabels.select,
        },
      ].concat(data.line.selectGroups),
    [data, labelManagerLabels]
  );

  const modelValueOpts = useMemo(() => lineModels?.map((model) => model.value), [lineModels]);
  const modelNameOpts = useMemo(
    () => [...new Set(lineModels?.map((model) => model.label))],
    [lineModels]
  );

  const loadModelli = useCallback(async () => {
    try {
      const response = await fetch(serviceLine);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(`Error calling ${serviceLine} ${response.status} ${response.statusText}`);
      }
      setModelli(data.GetProductsLinesResult);
    } catch (err) {
      console.log("Error loading modelli: ", err);
    }
    setLoading(false);
  }, [serviceLine]);

  useEffect(() => {
    if (!modelli) {
      loadModelli();
    }
  }, [modelli, loadModelli]);

  const searchManual = useCallback(
    async (id) => {
      setLoadingManuals(true);
      try {
        const [manuals, soc, docCe] = await Promise.all([
          getManuals(id, siteMetadata),
          getSocDoc(id, pathPrefix, labelManagerLabels),
          getDeclarationOfConformity(id, pathPrefix, labelManagerLabels),
        ]);
        setManuals(manuals.concat(soc).concat(docCe));
      } catch (err) {
        console.error(err);
      }
      setLoadingManuals(false);
    },
    [siteMetadata, labelManagerLabels, pathPrefix]
  );

  const [attributes, setAttributes] = useState();

  const loadAttribute = useCallback(
    async (id) => {
      try {
        const response = await fetch(
          `${serviceBaseUrl}/getproductdetailsnew/${id}/${siteMetadata.babelPrefix}`
        );
        const data = await response.json();
        if (data && data.GetProductDetailsResult) {
          setAttributes(data.GetProductDetailsResult.attributes);
        }
      } catch (err) {
        console.error(err);
        setAttributes({});
      }
    },
    [siteMetadata]
  );

  const productApp = useMemo(() => {
    if (attributes) {
      return liferayProductApp.find((item) => {
        return attributes.some(
          (attr) => `${attr.id}` === item.paIdAttribute && `${attr.id_option}` === item.paIdOption
        );
      });
    }
  }, [attributes, liferayProductApp]);

  useEffect(() => {
    if (!!product?.value) {
      searchManual(product.value);
      loadAttribute(product.value);
    }
  }, [product, loadAttribute, searchManual]);

  const onChange = useCallback(
    (e) => {
      if (e.target.name === "lineOption") {
        setLineOption(e.target.value);
        setProduct(null);
        setAttributes(null);
        setManuals(null);
      }

      if (e.target.name === "assistenza_manuale_modello") {
        const prod = lineModels?.find((model) => model.label === e.target.value);
        setAttributes(null);
        setManuals(null);
        setProduct(prod);
      }

      if (e.target.name === "productCode") {
        const prod = lineModels?.find((model) => model.value === e.target.value);
        setAttributes(null);
        setManuals(null);
        setProduct(prod);
      }
    },
    [lineModels]
  );

  const formInfo = useMemo(
    () => ({
      formData: {
        lineOption,
        assistenza_manuale_modello: product?.label,
        productCode: product?.value,
      },
      onChange,
      formErrors,
      setFormErrors,
    }),
    [lineOption, formErrors, product, onChange]
  );

  const showHelpSection = line && !`${line}`.startsWith("99");

  return (
    <div className="manuali">
      <GroupProvider data={formInfo}>
        <section
          className={classNames("section section--dark", {
            "mb-0": !showHelpSection,
          })}
        >
          {loading ? (
            <div className="d-flex justify-content-center align-items-center py-4">
              <div className="spinner-border"></div>
            </div>
          ) : (
            <div className="container">
              <div className="row text-center">
                <div className="col-12">
                  <p>{labelManagerLabels.formProductLine}</p>
                  <FormSelect
                    id="line"
                    field="lineOption"
                    label={labelManagerLabels.formProductLine}
                    options={lineOpts}
                    className="manuali__select manuali__select--lines"
                  />
                  {!modelli && !loading && <p>{labelManagerLabels.bookletNotAvaiable}</p>}
                </div>
              </div>
            </div>
          )}
        </section>
        {line && (
          <>
            {showHelpSection && (
              <section className="section" data-line={line}>
                <div className="container">
                  <div className="row text-center">
                    <div className="col-12">
                      <p>{labelManagerLabels.bookletFindImage}</p>
                      <div className="manuali__contenuto">
                        <img
                          alt={labelManagerLabels.formProductLine}
                          src={`${CDN_BASE_URL}/booklet/libretto-${line}.png`}
                        />
                        <img
                          alt={labelManagerLabels.howReadProductCode}
                          src={`${CDN_BASE_URL}/booklet/libretto-label.png`}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            )}
            <section className="section section--dark">
              <div className="container">
                <div className="row text-center">
                  <div className="col-12">
                    <p>{labelManagerLabels.bookletSelectModel}</p>
                    <div className="d-inline-flex flex-column align-items-end">
                      {lineModels.length ? (
                        <FormAutocomplete
                          id="model"
                          field="assistenza_manuale_modello"
                          options={modelNameOpts}
                          isRequired={true}
                          label={labelManagerLabels.formModel}
                          className="manuali__input"
                        />
                      ) : (
                        <div>{labelManagerLabels.bookletNotFound}</div>
                      )}
                      <div className="mt-3" id="oppure_codice">
                        <FormAutocomplete
                          id="inputModel"
                          field="productCode"
                          options={modelValueOpts}
                          isRequired={true}
                          label={labelManagerLabels.bookletProductCode}
                          className="manuali__input"
                          maxLength="40"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            {manuals ? (
              <section className="section mb-4">
                <div className="container">
                  <div className="row text-center">
                    <div className="col-12">
                      {manuals?.length > 0 ? (
                        <>
                          <p className="manuali__info-results">
                            {labelManagerLabels.bookletFound}
                            <span className="modello">
                              "{!!product?.label ? product.label : product?.value}"
                            </span>
                          </p>
                          <div className="manuali__box-risultati">
                            {manuals?.map((docType, index) => (
                              <ManualResult
                                key={index}
                                docType={docType}
                                open={manuals?.length === 1 ? true : false}
                                labelManagerLabels={labelManagerLabels}
                              />
                            ))}
                          </div>
                        </>
                      ) : (
                        <div className="manuali__info-noresults">
                          {Parser(data.bookletRequest, {
                            replace: (node) => {
                              if (node.type === "tag" && node.name === "a" && node.attribs?.href) {
                                const url = node.attribs.href;
                                const [baseUrl] = url.replace(/^\/\w\w_\w\w\//, "/").split("?");
                                const params = new URLSearchParams();
                                params.append("lineId", line);
                                params.append("model", product?.label);
                                params.append("productId", product?.value);
                                return (
                                  <Link to={`${baseUrl}?${params.toString()}`}>
                                    {node.children[0].data}
                                  </Link>
                                );
                              }
                            },
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </section>
            ) : (
              loadingManuals && (
                <div className="d-flex justify-content-center align-items-center py-4">
                  <div className="spinner-border"></div>
                </div>
              )
            )}
            {productApp && (
              <section className="section section--app">
                <BannerPushApp data={productApp} position="booklet" />
              </section>
            )}
          </>
        )}
      </GroupProvider>
    </div>
  );
};

export default Manuali;
export const fragment = graphql`
  fragment BookletFragment on LiferayBooklet {
    id
    liferayFields {
      siteId
      articleId
    }
    line {
      ... on LiferayLinesMultivalue {
        selectGroups {
          label
          group {
            label
            value
          }
        }
      }
    }
    booklet
    bookletRequest
  }
`;
